import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Plus, RefreshCcw, Trash2, X } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import VoicePicker from '../components/VoicePicker.jsx';
import { demonstrationsAPI } from '../services/api.js';
import { formatDateTime } from '../lib/format.js';

// A demo line is named after the business, but with a suffix — "Sugar Land Modern
// Dentistry (demo)". The CONTACT in the campaign is just "Sugar Land Modern
// Dentistry", so searching the demo name verbatim finds nothing. Strip the trailing
// parenthetical before handing it to the business search.
const businessNameOf = (demoName) =>
  String(demoName || '').replace(/\s*\((demo|test)[^)]*\)\s*$/i, '').trim();

// Demonstrations — a LIGHTWEIGHT, short-lived inbound demo (NOT a full tenant):
// its own number + 1–3 AIs + a primed system prompt + an opening line + an
// editable "expires in N days". The owner assigns a number, hands it out, and
// lets it auto-expire. (The 833 Bacardi line is the first demo to migrate in.)

const STATUS_BADGE = {
  active: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  expired: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  disabled: 'bg-stone-800 text-stone-400 border border-stone-700'
};

const MAX_AIS = 3;

const emptyAi = () => ({ name: '', voiceId: '', voiceProvider: 'elevenlabs' });

const emptyDraft = () => ({
  name: '',
  phoneNumber: '',
  roster: [emptyAi()],
  systemPrompt: '',
  openingLine: '',
  expiresInDays: '',
  status: 'active',
  // Spoken to a caller when the demo is RETIRED (status='ended'), before hangup.
  closingMessage: ''
});

// Map a list row (or a new blank) into the editable form draft.
function rowToDraft(row) {
  if (!row) return emptyDraft();
  return {
    name: row.name || '',
    phoneNumber: row.phoneNumber || '',
    roster: row.roster?.length ? row.roster.map((r) => ({ ...r })) : [emptyAi()],
    systemPrompt: row.systemPrompt || '',
    openingLine: row.openingLine || '',
    expiresInDays: row.expiresInDays == null ? '' : String(row.expiresInDays),
    // Preserve the manual terminal states ('disabled', 'ended') so editing an
    // ENDED demo doesn't silently show it as Active and un-retire it on save.
    // 'expired' still maps to 'active' (it auto-reverts on the clock).
    status: ['disabled', 'ended'].includes(row.status) ? row.status : 'active',
    closingMessage: row.closingMessage || ''
  };
}

function daysLeftLabel(d) {
  if (d.effectiveStatus === 'ended') return 'Retired';
  if (d.effectiveStatus === 'disabled') return 'Disabled';
  if (d.expiresInDays == null) return 'No expiry';
  if (d.daysLeft === 0 || d.effectiveStatus === 'expired') return 'Expired';
  return `${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'} left`;
}

export default function Demonstrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // editing: null = closed, 'new' = create, or a row object = edit
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await demonstrationsAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load demonstrations.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyDraft()); setEditing('new'); setError(''); };
  const openEdit = (row) => { setDraft(rowToDraft(row)); setEditing(row); setError(''); };
  const closeForm = () => { setEditing(null); setDraft(emptyDraft()); };

  // ── roster (1–3 AIs) editing ──
  const setAi = (i, patch) =>
    setDraft((d) => ({ ...d, roster: d.roster.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const addAi = () =>
    setDraft((d) => (d.roster.length >= MAX_AIS ? d : { ...d, roster: [...d.roster, emptyAi()] }));
  const removeAi = (i) =>
    setDraft((d) => (d.roster.length <= 1 ? d : { ...d, roster: d.roster.filter((_, j) => j !== i) }));

  const save = async () => {
    // Build the payload, trimming + dropping empties so the API's nullable/optional
    // fields behave. roster is required (1–3 named AIs with a voice).
    const roster = draft.roster
      .map((a) => ({
        name: a.name.trim(),
        voiceId: (a.voiceId || '').trim(),
        voiceProvider: a.voiceProvider || 'elevenlabs'
      }))
      .filter((a) => a.name && a.voiceId);

    if (!draft.name.trim()) { setError('Name is required.'); return; }
    if (roster.length < 1) { setError('Add at least one AI with a name and a voice.'); return; }
    if (!draft.systemPrompt.trim()) { setError('A primed system prompt is required.'); return; }

    const payload = {
      name: draft.name.trim(),
      phoneNumber: draft.phoneNumber.trim() || null,
      roster,
      systemPrompt: draft.systemPrompt.trim(),
      openingLine: draft.openingLine.trim() || null,
      expiresInDays: draft.expiresInDays === '' ? null : Number(draft.expiresInDays),
      status: draft.status,
      // Only meaningful when retired; sent always so clearing it persists.
      closingMessage: draft.closingMessage.trim() || null
    };

    // Retiring with no message FAILS OPEN server-side (the demo would keep running
    // normally instead of speaking a retirement message) — warn so it isn't a
    // silent no-op, but allow it (an intentional plain shutdown is valid).
    if (draft.status === 'ended' && !payload.closingMessage) {
      setError('Retired with no closing message — the line will just keep running as-is. Add a message for the AI to speak, or switch to “Disabled” to turn it off.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (editing === 'new') {
        await demonstrationsAPI.create(payload);
      } else {
        await demonstrationsAPI.update(editing.id, payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete demo "${row.name}"? This cannot be undone.`)) return;
    try {
      await demonstrationsAPI.remove(row.id);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-5xl">
      <PageHeader
        title="Demonstrations"
        subtitle="Lightweight, short-lived demo lines — a number + 1–3 AIs + a primed prompt that auto-expires"
        actions={
          <>
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCcw size={14} /> Refresh
            </button>
            <button type="button" className="btn-primary" onClick={openNew}>
              <Plus size={14} /> New demo
            </button>
          </>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      {editing && (
        <div className="card mb-6 border-alfred-gold/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-stone-100 font-medium">
              {editing === 'new' ? 'New demonstration' : `Edit: ${editing.name}`}
            </h2>
            <button type="button" className="btn-ghost" onClick={closeForm} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="field-label">Name</span>
                <input
                  className="field-input"
                  placeholder="Bacardi / Michael demo"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="field-label">Number (E.164, optional)</span>
                <input
                  className="field-input"
                  placeholder="+18335551234"
                  value={draft.phoneNumber}
                  onChange={(e) => setDraft({ ...draft, phoneNumber: e.target.value })}
                />
              </label>
            </div>

            {/* AIs (1–3) via the vendor-aware VoicePicker */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="field-label mb-0">AIs ({draft.roster.length}/{MAX_AIS})</span>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={addAi}
                  disabled={draft.roster.length >= MAX_AIS}
                >
                  <Plus size={12} /> Add AI
                </button>
              </div>
              <div className="space-y-2">
                {draft.roster.map((ai, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <input
                      className="field-input flex-1"
                      placeholder={`AI ${i + 1} name (speaker tag)`}
                      value={ai.name}
                      onChange={(e) => setAi(i, { name: e.target.value })}
                    />
                    <div className="flex-1">
                      <VoicePicker
                        provider={ai.voiceProvider}
                        voiceId={ai.voiceId}
                        onChange={({ provider, voiceId }) =>
                          setAi(i, { voiceProvider: provider || 'elevenlabs', voiceId: voiceId || '' })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-ghost shrink-0 mt-0.5"
                      onClick={() => removeAi(i)}
                      disabled={draft.roster.length <= 1}
                      aria-label="Remove AI"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="field-label">Primed system prompt</span>
              <textarea
                className="field-input min-h-[140px]"
                placeholder="The tuned panel prompt this demo runs on…"
                value={draft.systemPrompt}
                onChange={(e) => setDraft({ ...draft, systemPrompt: e.target.value })}
              />
            </label>

            <label className="block">
              <span className="field-label">Opening line (optional)</span>
              <input
                className="field-input"
                placeholder="What the AI leads with when the call connects"
                value={draft.openingLine}
                onChange={(e) => setDraft({ ...draft, openingLine: e.target.value })}
              />
            </label>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="field-label">Expires in __ days</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  className="field-input"
                  placeholder="Leave blank for no expiry"
                  value={draft.expiresInDays}
                  onChange={(e) => setDraft({ ...draft, expiresInDays: e.target.value })}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Computed from the create date. Bump this to re-activate an expired demo.
                </span>
              </label>
              <label className="block">
                <span className="field-label">Status</span>
                <select
                  className="field-input"
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled (manual off-switch)</option>
                  {/* Retire only applies to an existing demo — the create API doesn't
                      accept 'ended' (you retire a demo that already exists). */}
                  {editing !== 'new' && (
                    <option value="ended">Retired (speak a message, then hang up)</option>
                  )}
                </select>
                <span className="text-xs text-stone-500 mt-1 block">
                  "Expired" is computed automatically — it isn't set here. "Retired"
                  keeps the number answering to play your closing message below.
                </span>
              </label>
            </div>

            {draft.status === 'ended' && (
              <label className="block">
                <span className="field-label">Closing message (spoken on the retired line)</span>
                <textarea
                  className="field-input"
                  rows={3}
                  placeholder="e.g. Thanks for calling — this demo has wrapped up. Reach Brian at brian@muleworks.co and he'll get you set up."
                  value={draft.closingMessage}
                  onChange={(e) => setDraft({ ...draft, closingMessage: e.target.value })}
                  maxLength={1000}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  The AI speaks this to anyone who calls the retired number, then hangs up.
                </span>
              </label>
            )}

            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editing === 'new' ? 'Create demo' : 'Save changes'}
              </button>
              <button type="button" className="btn-ghost" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Number</th>
              <th className="px-4 py-3 font-medium">AIs</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Window</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                No demonstrations yet. Click <em>New demo</em> to set one up.
              </td></tr>
            ) : items.map((d) => (
              <tr
                key={d.id}
                onClick={() => openEdit(d)}
                className="border-b border-stone-900 hover:bg-stone-900/50 cursor-pointer transition"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-alfred-gold" />
                    {/* Brian: "I want to be able to target their history by clicking on
                        their name from the demonstration area" — instead of copying the
                        name out and pasting it into the search on another page.
                        stopPropagation because the row itself opens the demo editor. */}
                    {businessNameOf(d.name) ? (
                      <Link
                        to={`/campaigns?business=${encodeURIComponent(businessNameOf(d.name))}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-stone-100 font-medium underline-offset-2 hover:text-alfred-gold hover:underline"
                        title={`Find call history for ${businessNameOf(d.name)}`}
                      >
                        {d.name}
                      </Link>
                    ) : (
                      <span className="text-stone-100 font-medium">{d.name}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-300 tabular-nums whitespace-nowrap">
                  {d.phoneNumber || <span className="text-stone-600">—</span>}
                </td>
                <td className="px-4 py-3 text-stone-400 truncate max-w-[180px]">
                  {(d.roster || []).map((r) => r.name).join(', ') || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`badge ${STATUS_BADGE[d.effectiveStatus] || ''}`}>
                    {d.effectiveStatus}
                  </span>
                </td>
                <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{daysLeftLabel(d)}</td>
                <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{formatDateTime(d.createdAt)}</td>
                <td className="px-4 py-3 text-stone-500">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={(e) => { e.stopPropagation(); remove(d); }}
                    aria-label="Delete demo"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
